import React, { useEffect, useState } from 'react';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibDeleteOutlineIcon } from '@chewy/kib-icons-react';
import KibDivider from '@chewy/kib-divider-react';
import { useMutation, useQueryClient } from 'react-query';
import FetchError from 'types/http';
import { KibSheet } from '@chewy/kib-overlays-react';
import { usePetForm } from '../PetFormContext/PetFormContext';
import {
  assignVetClinicApi,
  getPetClinics,
  handleVetClinicError,
  unassignVetClinicApi,
} from '../../../api/vetClinicApi';
import { scrollDocumentToVet } from '../util/scrollDocumentToVet';
import { scrollBody } from '../../../util/scrollBody';
import {
  removeClinicCheckerTitle,
  removeClinicCheckerText,
  vetClinicTitle,
  vetClinicIntro,
} from './vetContent';
import RemoveVetClinic from './RemoveVetClinic';
import { Clinic, Clinics } from './vetClinicResponse';
import AddVetClinic from './AddVetClinic';
import VetClinicCard from './VetClinicCard';
import './vet-clinic-list.scss';
import VetSnackbars from './VetSnackbars';
import ErrorListClinics from './ErrorListClinics';

export const ListVetClinic = ({ clinics }: { clinics: Clinics }) => {
  const {
    showVetListError,
    selectedRemoveClinic,
    setSelectedRemoveClinic,
    selectedUndoRemoveClinic,
    setSelectedUndoRemoveClinic,
    newClinic,
    setDisplayUnassignErrorSnackbar,
    setDisplayAssignErrorSnackbar,
    showUndoSnackbar,
    setShowUndoSnackbar,
  } = usePetForm();
  const [showRemoveVetSheet, setShowRemoveVetSheet] = useState(false);

  const [vetClinicList, setVetClinicList] = useState<Clinics>(clinics);

  const queryClient = useQueryClient();

  // When new Clinic is added from Onboarding Clinic
  useEffect(() => {
    !!newClinic && handleAddClinic(newClinic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newClinic]);

  // When saving profile before undo snackbar closes
  useEffect(() => {
    if (!showUndoSnackbar && !selectedUndoRemoveClinic && selectedRemoveClinic) {
      handleRemoveClinic(selectedRemoveClinic);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUndoSnackbar]);

  const invalidateVetQuery = () => {
    const invalidQueries = [queryClient.removeQueries(getPetClinics.key)];
    return Promise.all(invalidQueries);
  };

  const assignClinic = async (clinic: Clinic) => {
    try {
      await assignVetClinicApi.assign(clinic.id);
      invalidateVetQuery();
      return clinic;
    } catch (error) {
      setDisplayAssignErrorSnackbar(true);
      handleVetClinicError(error, 'assignClinic');
    }
  };

  const { mutate: assignMutate } = useMutation(assignClinic, {
    onSuccess: (clinic) => {
      if (clinic) {
        const updatedClinicList = vetClinicList.concat([clinic]);
        setVetClinicList(updatedClinicList);
      }
    },
    // Error handling to be added with https://chewyinc.atlassian.net/browse/PPF-974
    onError: (error: FetchError) => {
      throw new Error(`Failed to assign clinic. Status code: ${error.status}. Error: ${error}`);
    },
  });

  const handleAddClinic = async (clinic: Clinic) => {
    try {
      await assignMutate(clinic);
    } catch {
      throw new Error('Failed to assign clinic.');
    }
  };

  const handleUndoRemoveClinic = (clinic: Clinic) => {
    // local changes only
    const updatedClinicList = vetClinicList.concat([clinic]);
    setVetClinicList(updatedClinicList);
  };

  const unassignClinic = async (clinic: Clinic) => {
    try {
      await unassignVetClinicApi.unassign(clinic.id);
      invalidateVetQuery();
      return clinic;
    } catch {
      setDisplayUnassignErrorSnackbar(true);
      throw new Error('Failed to unassign clinic.');
    }
  };

  const { mutate: unassignMutate } = useMutation(unassignClinic, {
    onError: (error: FetchError) => {
      throw new Error(`Failed to unassign clinic. Status code: ${error.status}. Error: ${error}`);
    },
  });

  const handleRemoveClinic = async (clinic: Clinic) => {
    try {
      await unassignMutate(clinic);
    } catch {
      throw new Error('Failed to unassign clinic.');
    }
    selectedUndoRemoveClinic && setSelectedUndoRemoveClinic(false);
  };

  const handleCancelRemoveClinic = () => {
    setShowRemoveVetSheet(false);
  };

  const handleLocalRemoveClinic = () => {
    // removing locally only
    setVetClinicList((oldClinics: Clinics) =>
      oldClinics.filter((oldclinic: Clinic) => oldclinic !== selectedRemoveClinic),
    );
    setShowRemoveVetSheet(false);
    setShowUndoSnackbar(true);
  };

  return (
    <div>
      <KibDivider className="pet-form-vet-list__divider" />
      <div className="pet-form-vet-list__title">{vetClinicTitle}</div>
      {!!showVetListError ? (
        <ErrorListClinics />
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {vetClinicList && vetClinicList.length > 0 ? (
            <>
              <div className="pet-form-vet-list__intro">{vetClinicIntro}</div>
              <ul className="pet-form-vet-list__options" data-testid="pet-form-vet-list-container">
                {vetClinicList.map((clinic: Clinic, index: number) => (
                  <li
                    key={index}
                    className="pet-form-vet-list__card pet-form-vet-list__card-list"
                    data-testid="pet-form-vet-list-card"
                    aria-label={`Clinic: ${clinic.name}.`}
                  >
                    <VetClinicCard clinic={clinic} />
                    <span className="pet-form-vet-list__remove_button_container">
                      <KibButtonNew
                        type="button"
                        aria-label="Delete Clinic"
                        className="pet-form-vet-list__remove"
                        data-testid="pet-form-vet-list-remove"
                        onClick={() => {
                          setShowRemoveVetSheet(true);
                          showUndoSnackbar && setShowUndoSnackbar(false);
                          setSelectedRemoveClinic(clinic);
                        }}
                        onKeyDown={() => {
                          setShowRemoveVetSheet(true);
                        }}
                        icon={<KibDeleteOutlineIcon className="pet-form-vet-list__remove--icon" />}
                        iconOnly
                        emphasis="tertiary"
                      />
                    </span>
                  </li>
                ))}
                <li>
                  <AddVetClinic clinics={vetClinicList} setClinics={setVetClinicList} />
                </li>
              </ul>
              {selectedRemoveClinic && (
                <KibSheet
                  open={showRemoveVetSheet}
                  onClose={() => {
                    setShowRemoveVetSheet(false);
                    scrollBody(true);
                    scrollDocumentToVet();
                  }}
                  bottom
                  title={removeClinicCheckerTitle}
                  size="large"
                  data-testid="remove-vet-sheet"
                >
                  <div className="pet-form-vet-list__remove--text">{removeClinicCheckerText}</div>
                  <RemoveVetClinic
                    clinic={selectedRemoveClinic}
                    onCancelClick={handleCancelRemoveClinic}
                    onRemoveClick={handleLocalRemoveClinic}
                  />
                </KibSheet>
              )}
              <VetSnackbars handleUndoRemoveClinic={handleUndoRemoveClinic} />
            </>
          ) : (
            <>
              <div className="pet-form-vet-list__intro">{vetClinicIntro}</div>
              <AddVetClinic clinics={vetClinicList} setClinics={setVetClinicList} />
              <KibDivider />
              <VetSnackbars handleUndoRemoveClinic={handleUndoRemoveClinic} />
            </>
          )}
        </>
      )}
    </div>
  );
};
